import {DocumentStoreApi} from '@/components/document/store/document-store';
import {resolveReference} from '@/engine/values/reference';
import {LocalizedString, ValueDefinition} from '@/model/types';
import {mapValues} from '@/utils/object';
import {ValueResolver, ValueType, ValueResolverOptions} from '../types';
import {resolveGenerated} from './generated';
import {resolveQuery} from './query';
import {resolveTemplate} from './template';

export const createValueResolver = (store: DocumentStoreApi, values: ValueDefinition[]): ValueResolver => {
  return function resolver<T = string>(reference?: ValueType | ValueType[], options: ValueResolverOptions<T> = {}): T {
    const {locale, data} = store.getState();
    const {transform = input => input as T} = options;

    if (!reference) {
      return transform('');
    }

    if (typeof reference === 'string') {
      return transform(reference);
    }

    if (typeof reference === 'number') {
      return transform(reference.toFixed());
    }

    if (Array.isArray(reference)) {
      return reference.map(ref => resolver(ref, options)) as T;
    }

    if ('$template' in reference) {
      if (typeof reference.$template === 'string') {
        return transform(resolveTemplate(reference.$template, resolver, options, locale));
      }

      return resolver(
        mapValues(reference.$template, value => resolveTemplate(value, resolver, options, locale)) as LocalizedString,
        {
          transform,
        },
      );
    }

    if ('$query' in reference) {
      return resolveQuery(reference, data, resolver, options);
    }

    if ('$generated' in reference) {
      return resolveGenerated(reference, resolver, options);
    }

    if (!('$ref' in reference)) {
      return transform(reference[locale]);
    }

    return resolveReference(reference, store, values, resolver, options);
  };
};
